<template>
  <WTooltip
    v-if="shouldShowLabelTooltip"
    :section-tag="`${buttonDataTestid}-label-tooltip`"
    :description="iconLabel"
    intent="label"
    aria-mode="visual-label"
    size="hug"
    :show-arrow="false"
    :disabled="labelTooltipSuppressed"
  >
    <template #activator="{ props: tooltipActivatorProps }">
      <!--
        `.wbutton-disabled` sets pointer-events:none on the v-btn, and an element with
        pointer-events:none is skipped during hit-testing, so it never receives a real
        mouseenter. The tooltip binds to this wrapper instead, which stays hoverable.
      -->
      <span v-bind="tooltipActivatorProps" class="wbutton-tooltip-activator">
        <v-btn
          v-bind="forwardedAttrs"
          :data-testid="buttonDataTestid"
          :color="getColorButton"
          :variant="getVariantButton"
          :class="[attrs.class, getClass, { 'wbutton-loading': showLoading }]"
          :style="[attrs.style, customStyle]"
          :disabled="props.disabled"
          :aria-busy="showLoading ? 'true' : undefined"
          :tabindex="showLoading ? -1 : undefined"
          @click="handleClick"
        >
          <template v-if="slots.prepend" #prepend>
            <slot name="prepend"></slot>
          </template>
          <span :class="['wbutton-inner', { 'wbutton-is-loading': showLoading }]">
            <span class="wbutton-content">
              <v-icon
                v-if="icon && iconPosition == 'start'"
                :icon="getIcon(icon)"
                :data-testid="buttonDataTestid ? `${buttonDataTestid}-icon` : undefined"
                class="inside__icon-start"
              ></v-icon>
              <v-icon
                v-if="icon && iconPosition == 'center'"
                :icon="getIcon(icon)"
                :data-testid="buttonDataTestid ? `${buttonDataTestid}-icon` : undefined"
                class="inside__icon-center"
              ></v-icon>
              <span v-if="!isIconOnly" :class="getLabelClass">
                {{ label }}
              </span>
              <v-icon
                v-if="icon && iconPosition == 'end'"
                :icon="getIcon(icon)"
                :data-testid="buttonDataTestid ? `${buttonDataTestid}-icon` : undefined"
                class="inside__icon-end"
              ></v-icon>
              <span v-if="slots.append" class="wbutton-append">
                <slot name="append"></slot>
              </span>
            </span>
            <span v-if="showLoading" class="wbutton-loader" aria-hidden="true">
              <WLoader decorative :section-tag="`${buttonDataTestid}-loader`" :color="loaderProps.color" />
            </span>
          </span>
        </v-btn>
      </span>
    </template>
  </WTooltip>
  <v-btn
    v-else
    v-bind="forwardedAttrs"
    :data-testid="buttonDataTestid"
    :color="getColorButton"
    :variant="getVariantButton"
    :class="[attrs.class, getClass, { 'wbutton-loading': showLoading }]"
    :style="[attrs.style, customStyle]"
    :disabled="props.disabled"
    :aria-busy="showLoading ? 'true' : undefined"
    :tabindex="showLoading ? -1 : undefined"
    @click="handleClick"
  >
    <template v-if="slots.prepend" #prepend>
      <slot name="prepend"></slot>
    </template>
    <span :class="['wbutton-inner', { 'wbutton-is-loading': showLoading }]">
      <span class="wbutton-content">
        <v-icon
          v-if="icon && iconPosition == 'start'"
          :icon="getIcon(icon)"
          :data-testid="buttonDataTestid ? `${buttonDataTestid}-icon` : undefined"
          class="inside__icon-start"
        ></v-icon>
        <v-icon
          v-if="icon && iconPosition == 'center'"
          :icon="getIcon(icon)"
          :data-testid="buttonDataTestid ? `${buttonDataTestid}-icon` : undefined"
          class="inside__icon-center"
        ></v-icon>
        <span v-if="!isIconOnly" :class="getLabelClass">
          {{ label }}
        </span>
        <v-icon
          v-if="icon && iconPosition == 'end'"
          :icon="getIcon(icon)"
          :data-testid="buttonDataTestid ? `${buttonDataTestid}-icon` : undefined"
          class="inside__icon-end"
        ></v-icon>
        <span v-if="slots.append" class="wbutton-append">
          <slot name="append"></slot>
        </span>
      </span>
      <span v-if="showLoading" class="wbutton-loader" aria-hidden="true">
        <WLoader decorative :section-tag="`${buttonDataTestid}-loader`" :color="loaderProps.color" />
      </span>
    </span>
  </v-btn>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, useAttrs, useSlots, type CSSProperties } from 'vue'
import { tokens } from '@/plugins/vuetify'
import { WLoader, WTooltip } from '@wasabi/data-visualization'
import { WTooltipSuppressLabelKey } from '@/components/wasabi/data-visualization/tooltipContext'
import { mdiCloseBox, mdiDeleteOutline, mdiPencil, mdiPlus } from '@mdi/js'

export interface WButtonProps {
  sectionTag?: string
  variant?: string
  size?: string
  label?: string
  labelClass?: string
  icon?: string
  iconPosition?: string
  iconBorder?: boolean
  iconOnly?: boolean
  loading?: boolean
  disabled?: boolean
  insideComponent?: boolean
  color?: string | null
  customStyle?: CSSProperties
  showLabelTooltip?: boolean
  ariaLabel?: string
  /** Controlled toggle state for secondary buttons. WButton supplies aria-pressed unless the caller does. */
  pressed?: boolean
}

defineOptions({
  inheritAttrs: false
})

const props = withDefaults(defineProps<WButtonProps>(), {
  sectionTag: 'unspecified',
  variant: 'primary',
  size: 'large',
  label: '',
  labelClass: '',
  icon: '',
  iconPosition: 'start',
  iconBorder: false,
  iconOnly: false,
  loading: false,
  disabled: false,
  insideComponent: false,
  color: null,
  customStyle: () => ({}),
  showLabelTooltip: true,
  ariaLabel: undefined,
  pressed: undefined
})

const slots = useSlots()
const attrs = useAttrs()
const labelTooltipSuppressed = inject(
  WTooltipSuppressLabelKey,
  computed(() => false)
)

const emit = defineEmits(['click'])

type ButtonAttrs = Record<string, unknown>

const isLegacyIconVariant = computed(() => {
  return props.variant == 'icon' || props.variant == 'icon-secondary'
})

const isIconOnly = computed(() => isLegacyIconVariant.value || props.iconOnly)
const supportsPressedState = computed(() => props.variant == 'secondary')

const iconLabel = computed(() => props.label.trim())
const explicitAriaLabel = computed(() => props.ariaLabel?.trim() ?? '')

const hasManagedLabelTooltip = computed(() => {
  return isIconOnly.value && props.showLabelTooltip && iconLabel.value.length > 0
})

const shouldShowLabelTooltip = computed(() => hasManagedLabelTooltip.value)

const buttonDataTestid = computed(() => {
  const dataTestid = attrs['data-testid']
  if (typeof dataTestid === 'string' && dataTestid.trim()) return dataTestid

  return props.sectionTag
})

const hasAttrValue = (value: unknown): boolean => {
  return typeof value === 'string' ? value.trim().length > 0 : value !== undefined && value !== null
}

const hasExplicitAccessibleName = computed(() => {
  return (
    hasAttrValue(explicitAriaLabel.value) || hasAttrValue(attrs['aria-label']) || hasAttrValue(attrs['aria-labelledby'])
  )
})

const forwardedAttrs = computed<ButtonAttrs>(() => {
  const nextAttrs = { ...attrs }

  delete nextAttrs.class
  delete nextAttrs.style
  delete nextAttrs['data-testid']

  if (hasManagedLabelTooltip.value) {
    delete nextAttrs.title
  }

  if (explicitAriaLabel.value) {
    nextAttrs['aria-label'] = explicitAriaLabel.value
  } else if (isIconOnly.value && iconLabel.value && !hasExplicitAccessibleName.value) {
    nextAttrs['aria-label'] = iconLabel.value
  }

  if (supportsPressedState.value && props.pressed !== undefined && !hasAttrValue(nextAttrs['aria-pressed'])) {
    nextAttrs['aria-pressed'] = String(props.pressed)
  }

  return nextAttrs
})

const getIcon = (iconName: string): string => {
  switch (iconName) {
    case 'mdi-CloseBox':
      return mdiCloseBox
    case 'mdi-Pen':
      return mdiPencil
    case 'mdi-plus':
      return mdiPlus
    case 'mdi-delete-outline':
      return mdiDeleteOutline
    default:
      // Modern callers pass SVG path strings directly from `@mdi/js` (start with `M`).
      // Only warn when the caller passed a legacy `mdi-*` string name we couldn't map.
      if (iconName.startsWith('mdi-')) {
        console.warn(`Icon ${iconName} not found in map, passing icon directly`)
      }
      return iconName
  }
}

const showLoading = computed(() => {
  if (props.variant == 'link' || isIconOnly.value) {
    return false
  }
  return props.loading
})

const getVariantButton = computed(() => {
  if (props.variant == 'secondary' || props.variant == 'action') {
    return 'outlined'
  } else if (props.variant == 'utility' || props.variant == 'utility-lite') {
    return 'flat'
  } else if (props.variant == 'link' || props.variant == 'tertiary') {
    return 'text'
  } else if (isLegacyIconVariant.value) {
    if (props.iconBorder && props.size == 'large') {
      return 'outlined'
    } else {
      return 'text'
    }
  }
  return 'flat'
})

const getColorButton = computed(() => {
  if (props.color) {
    return props.color
  }
  if (props.variant == 'icon') {
    return tokens.color.content.text.primary
  } else if (props.variant == 'secondary') {
    return tokens.color.action.secondary.text
  } else if (props.variant == 'action') {
    return tokens.color.content.text.primary
  } else if (props.variant == 'tertiary') {
    return tokens.color.action.tertiary.text
  } else if (props.variant == 'utility' || props.variant == 'utility-lite') {
    return tokens.color.action.utility.text
  } else if (props.variant == 'destructive') {
    return tokens.color.action.destructive.surface
  }
  return tokens.color.action.primary.surface
})

const getClass = computed(() => {
  let current = ''
  if (isLegacyIconVariant.value) {
    current = 'wbutton-icon'
    if (props.insideComponent) {
      current += ' wbutton-inside-component'
    }
  } else if (props.iconOnly) {
    current = 'wbutton-icon-only'
  }
  if (props.variant == 'primary') {
    current += ' w-button-primary'
  } else if (props.variant == 'secondary') {
    current += ' w-button-secondary'
  } else if (props.variant == 'link') {
    current += ' w-button-link'
  } else if (props.variant == 'icon-secondary') {
    current += ' wbutton-icon-secondary'
  } else if (props.variant == 'action') {
    current += ' wbutton-action'
  } else if (props.variant == 'tertiary') {
    current += ' w-button-tertiary'
  } else if (props.variant == 'utility') {
    current += ' w-button-utility'
  } else if (props.variant == 'utility-lite') {
    current += ' w-button-utility-lite'
  } else if (props.variant == 'destructive') {
    current += ' w-button-destructive'
  }
  if (props.size == 'small') {
    current += ' wbutton-small'
  } else {
    current += ' wbutton-large'
  }
  if (props.disabled) {
    current += ' wbutton-disabled'
  }
  if (supportsPressedState.value && props.pressed) {
    current += ' wbutton-pressed'
  }
  return current
})

const getDefaultLabelClass = () => {
  if (props.variant == 'action' || (props.variant == 'link' && props.size != 'small')) {
    return 'body-regular-default'
  } else if (props.size == 'large') {
    return 'action-semibold-medium'
  }
  return 'action-semibold-small'
}

const getLabelClass = computed(() => props.labelClass || getDefaultLabelClass())

const getDisabledColor = computed(() => {
  if (props.variant == 'secondary') {
    return tokens.color.action.secondary.disabled.surface
  }
  if (props.variant == 'tertiary') {
    return 'transparent'
  }
  return tokens.color.action.primary.disabled.surface
})

const loaderProps = computed<{ color: 'light' | 'dark' }>(() => {
  const usesPrimarySurface =
    props.variant == 'primary' ||
    props.variant == 'destructive' ||
    (supportsPressedState.value && props.pressed && !props.disabled)
  const color = usesPrimarySurface ? 'light' : 'dark'
  return { color }
})

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !showLoading.value) {
    const element = (event.currentTarget || event.target) as HTMLElement | null
    const shouldBlur = event.detail > 0
    nextTick(() => {
      if (shouldBlur) element?.blur()
      emit('click', event)
    })
  }
}

const getIconBorderedBackground = computed(() => {
  return props.iconBorder ? tokens.color.background.surface.default : 'transparent'
})

defineExpose({
  getDisabledColor,
  getIconBorderedBackground,
  handleClick
})

onMounted(() => {
  if (import.meta.env.DEV && isIconOnly.value && !hasExplicitAccessibleName.value && !iconLabel.value) {
    console.warn(
      `[WButton] icon-only variant="${props.variant}" button (sectionTag: "${props.sectionTag}") has no ariaLabel and no label — screen readers will have no accessible name. Pass :aria-label to fix.`
    )
  }
})
</script>

<style scoped>
.wbutton-large,
.wbutton-small {
  padding: 0 v-bind('tokens.spacing.inset.control.horizontal.default');
}

.wbutton-large {
  height: 40px;
}
.wbutton-small {
  height: 26px;
}
.wbutton-small:not(.wbutton-icon) {
  height: 32px;
}

.wbutton-inside-component {
  height: 22px;
}

/* Hugs the v-btn exactly (no layout shift) while staying pointer-events:auto so it can
   host the label tooltip's hover/focus activator even when the button itself goes
   pointer-events:none via .wbutton-disabled — see the activator template comment above. */
.wbutton-tooltip-activator {
  display: inline-flex;
  align-items: center;
}

.v-btn {
  padding: 0 v-bind('tokens.spacing.inset.control.horizontal.default');
  text-transform: none;
  --v-theme-overlay-multiplier: 0 !important;
  font-kerning: normal;
  letter-spacing: normal;
  border-radius: v-bind('tokens.borderRadius.control.medium') !important;
}

/* ─── Loading overlay ─── */
/* Keep original content in the DOM to hold button width; overlay the loader on top. */
.wbutton-inner {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.wbutton-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.wbutton-append {
  display: inline-flex;
  align-items: center;
  margin-inline-start: var(--spacing-gap-small);
}

.wbutton-loader {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.wbutton-is-loading .wbutton-content {
  visibility: hidden;
}

.wbutton-loading {
  pointer-events: none;
}

/* ─── Primary ─── */
.w-button-primary {
  &:hover {
    background-color: v-bind('tokens.color.action.primary.hover') !important;
    border-color: v-bind('tokens.color.action.primary.hover') !important;
    color: v-bind('tokens.color.action.primary.text') !important;
  }

  &:focus {
    background-color: v-bind('tokens.color.action.primary.surface') !important;
    border-color: v-bind('tokens.color.action.primary.surface') !important;
    outline: 2px solid v-bind('tokens.color.focus.ring') !important;
    color: v-bind('tokens.color.action.primary.text') !important;
    --v-theme-overlay-multiplier: 0 !important;
  }

  &:active {
    background-color: v-bind('tokens.color.action.primary.active') !important;
    border-color: v-bind('tokens.color.action.primary.active') !important;
  }

  &.wbutton-disabled {
    background-color: v-bind('tokens.color.action.primary.disabled.surface') !important;
    border-color: v-bind('tokens.color.action.primary.disabled.surface') !important;
    color: v-bind('tokens.color.action.primary.disabled.text') !important;
    outline: none !important;
  }
}

/* ─── Secondary ─── */
.w-button-secondary {
  background: v-bind('tokens.color.action.secondary.surface') !important;
  border-color: v-bind('tokens.color.action.secondary.border') !important;
  color: v-bind('tokens.color.action.secondary.text') !important;

  &:hover {
    background-color: v-bind('tokens.color.action.secondary.hover') !important;
    border-color: v-bind('tokens.color.action.secondary.border') !important;
    color: v-bind('tokens.color.action.secondary.text') !important;
  }

  &:focus {
    background-color: v-bind('tokens.color.action.secondary.surface') !important;
    border-color: v-bind('tokens.color.action.secondary.border') !important;
    outline: 2px solid v-bind('tokens.color.focus.ring') !important;
    color: v-bind('tokens.color.action.secondary.text') !important;
    --v-theme-overlay-multiplier: 0 !important;
  }

  &:active {
    background-color: v-bind('tokens.color.action.secondary.active') !important;
    border-color: v-bind('tokens.color.action.secondary.border') !important;
    color: v-bind('tokens.color.action.secondary.text') !important;
  }

  &.wbutton-disabled {
    background-color: v-bind('tokens.color.action.secondary.disabled.surface') !important;
    border-color: v-bind('tokens.color.action.secondary.disabled.border') !important;
    color: v-bind('tokens.color.action.secondary.disabled.text') !important;
    outline: none !important;
  }

  &.wbutton-pressed:not(.wbutton-disabled) {
    background-color: v-bind('tokens.color.action.primary.surface') !important;
    border-color: v-bind('tokens.color.action.primary.surface') !important;
    color: v-bind('tokens.color.action.primary.text') !important;

    &:hover {
      background-color: v-bind('tokens.color.action.primary.hover') !important;
      border-color: v-bind('tokens.color.action.primary.hover') !important;
      color: v-bind('tokens.color.action.primary.text') !important;
    }

    &:focus {
      background-color: v-bind('tokens.color.action.primary.surface') !important;
      border-color: v-bind('tokens.color.action.primary.surface') !important;
      color: v-bind('tokens.color.action.primary.text') !important;
    }

    &:active {
      background-color: v-bind('tokens.color.action.primary.active') !important;
      border-color: v-bind('tokens.color.action.primary.active') !important;
      color: v-bind('tokens.color.action.primary.text') !important;
    }
  }
}

/* ─── Tertiary ─── */
.w-button-tertiary {
  background: transparent !important;

  &:hover {
    background-color: v-bind('tokens.color.action.tertiary.hover') !important;
    color: v-bind('tokens.color.action.tertiary.text') !important;
  }

  &:focus {
    background-color: transparent !important;
    outline: 2px solid v-bind('tokens.color.focus.ring') !important;
    color: v-bind('tokens.color.action.tertiary.text') !important;
    --v-theme-overlay-multiplier: 0 !important;
  }

  &:active {
    background-color: v-bind('tokens.color.action.tertiary.active') !important;
  }

  &.wbutton-disabled {
    background: transparent !important;
    color: v-bind('tokens.color.action.tertiary.disabled.text') !important;
    outline: none !important;
  }
}

/* ─── Utility ─── */
.w-button-utility {
  background-color: v-bind('tokens.color.action.utility.surface') !important;
  color: v-bind('tokens.color.action.utility.text') !important;

  &:hover {
    background-color: v-bind('tokens.color.action.utility.hover') !important;
    color: v-bind('tokens.color.action.utility.text') !important;
  }

  &:focus {
    background-color: v-bind('tokens.color.action.utility.surface') !important;
    outline: 2px solid v-bind('tokens.color.focus.ring') !important;
    color: v-bind('tokens.color.action.utility.text') !important;
    --v-theme-overlay-multiplier: 0 !important;
  }

  &:active {
    background-color: v-bind('tokens.color.action.utility.active') !important;
  }

  &.wbutton-disabled {
    background-color: v-bind('tokens.color.action.utility.disabled.surface') !important;
    color: v-bind('tokens.color.action.utility.disabled.text') !important;
    outline: none !important;
  }
}

/* ─── Utility Lite ─── */
.w-button-utility-lite {
  background-color: v-bind('tokens.color.background.surface.default') !important;
  color: v-bind('tokens.color.action.utility.text') !important;

  &:hover {
    background-color: v-bind('tokens.color.action.utility.hover') !important;
    color: v-bind('tokens.color.action.utility.text') !important;
  }

  &:focus {
    background-color: v-bind('tokens.color.background.surface.default') !important;
    outline: 2px solid v-bind('tokens.color.focus.ring') !important;
    color: v-bind('tokens.color.action.utility.text') !important;
    --v-theme-overlay-multiplier: 0 !important;
  }

  &:active {
    background-color: v-bind('tokens.color.action.utility.active') !important;
  }

  &.wbutton-disabled {
    background-color: v-bind('tokens.color.action.utility.disabled.surface') !important;
    color: v-bind('tokens.color.action.utility.disabled.text') !important;
    outline: none !important;
  }
}

/* ─── Destructive ─── */
.w-button-destructive {
  &:hover {
    background-color: v-bind('tokens.color.action.destructive.hover') !important;
    border-color: v-bind('tokens.color.action.destructive.hover') !important;
    color: v-bind('tokens.color.action.destructive.text') !important;
  }

  &:focus {
    background-color: v-bind('tokens.color.action.destructive.surface') !important;
    border-color: v-bind('tokens.color.action.destructive.surface') !important;
    outline: 2px solid v-bind('tokens.color.focus.ringDestructive') !important;
    color: v-bind('tokens.color.action.destructive.text') !important;
    --v-theme-overlay-multiplier: 0 !important;
  }

  &:active {
    background-color: v-bind('tokens.color.action.destructive.active') !important;
    border-color: v-bind('tokens.color.action.destructive.active') !important;
  }

  &.wbutton-disabled {
    background-color: v-bind('tokens.color.action.primary.disabled.surface') !important;
    border-color: v-bind('tokens.color.action.primary.disabled.surface') !important;
    color: v-bind('tokens.color.action.primary.disabled.text') !important;
    outline: none !important;
  }
}

/* ─── Link (legacy) ─── */
.w-button-link {
  background: transparent !important;
  border: none !important;
  text-transform: none !important;
  color: v-bind('tokens.color.action.tertiary.text');
  --v-theme-overlay-multiplier: 0 !important;
  padding-left: unset;
  padding-right: unset;

  &:hover {
    color: v-bind('tokens.color.action.primary.active') !important;
    background: transparent !important;
  }
  &:focus {
    background: none !important;
    color: v-bind('tokens.color.action.tertiary.text') !important;
    text-decoration: underline;
    outline: none !important;
  }

  &.wbutton-disabled {
    background: none !important;

    &:focus {
      background-color: transparent !important;
      text-decoration: none;
    }
  }
}

/* ─── Action (legacy) ─── */
.wbutton-action {
  background: v-bind('tokens.color.input.surface.default') !important;
  border-color: v-bind('tokens.color.input.border.default') !important;
  transition-duration: 120ms;

  &:hover {
    background-color: v-bind('tokens.color.input.surface.hover') !important;
    border-color: v-bind('tokens.color.input.border.default') !important;
    color: v-bind('tokens.color.content.text.primary') !important;
    outline: none !important;
    --v-theme-overlay-multiplier: 0 !important;
  }

  &:focus {
    background-color: v-bind('tokens.color.input.surface.default') !important;
    border-color: v-bind('tokens.color.input.border.default') !important;
    color: v-bind('tokens.color.content.text.primary') !important;
    outline: none !important;
    --v-theme-overlay-multiplier: 0 !important;
  }

  &:focus-visible,
  &[aria-expanded='true'] {
    border-color: v-bind('tokens.color.input.border.focus') !important;
    box-shadow: 0 0 0 3px v-bind('tokens.color.focus.ring');
  }
}

@media (forced-colors: active) {
  .w-button-secondary.wbutton-pressed:not(.wbutton-disabled),
  .w-button-secondary.wbutton-pressed:not(.wbutton-disabled):is(:hover, :focus, :active) {
    background-color: highlight !important;
    border-color: highlight !important;
    color: highlighttext !important;
    forced-color-adjust: none;
  }

  .w-button-secondary.wbutton-pressed:not(.wbutton-disabled):focus-visible {
    outline: 2px solid buttontext !important;
    outline-offset: 2px;
  }

  .wbutton-action:focus-visible {
    outline: 2px solid buttontext !important;
    outline-offset: 2px;
  }
}

/* ─── Disabled (generic fallback for legacy variants) ─── */
/* Vuetify dims its own `v-btn--disabled` to opacity .26, and only resets it for the `elevated` and
   `flat` variants — so the `outlined` (secondary/action) and `text` (tertiary/link/icon) variants
   would render washed out on top of the Wasabi disabled tokens. The tokens are the disabled look. */
.wbutton-disabled {
  background-color: v-bind('getDisabledColor') !important;
  border-color: v-bind('tokens.color.action.primary.disabled.surface') !important;
  outline: none !important;
  color: v-bind('tokens.color.action.primary.disabled.text') !important;
  opacity: 1 !important;
  pointer-events: none;

  &:focus {
    background-color: v-bind('getDisabledColor') !important;
    color: v-bind('tokens.color.action.primary.disabled.text') !important;
    outline: none !important;
  }
}

/* ─── Icons ─── */
.inside__icon-start {
  margin-right: 8px;
  font-size: 18px; /* TODO: no exact typography class match — icon sizing, not text */
}

.inside__icon-center {
  font-size: 18px; /* TODO: no exact typography class match — icon sizing, not text */
}

.inside__icon-end {
  margin-left: 8px;
  font-size: 18px; /* TODO: no exact typography class match — icon sizing, not text */
}

.w-button-link :deep(.inside__icon-start),
.w-button-link :deep(.inside__icon-end) {
  margin-right: 0;
  margin-left: 0;
}

.wbutton-small:not(.wbutton-icon):not(.wbutton-icon-only) .wbutton-content {
  gap: v-bind('tokens.spacing.gap.icon');
}

.wbutton-small:not(.wbutton-icon):not(.wbutton-icon-only) .inside__icon-start,
.wbutton-small:not(.wbutton-icon):not(.wbutton-icon-only) .inside__icon-center,
.wbutton-small:not(.wbutton-icon):not(.wbutton-icon-only) .inside__icon-end {
  width: 14px;
  height: 14px;
  margin-right: 0;
  margin-left: 0;
  font-size: 14px;
}

.wbutton-small.w-button-link:not(.wbutton-disabled) :deep(.inside__icon-start),
.wbutton-small.w-button-link:not(.wbutton-disabled) :deep(.inside__icon-center),
.wbutton-small.w-button-link:not(.wbutton-disabled) :deep(.inside__icon-end) {
  color: v-bind('tokens.color.action.secondary.text');
}

.wbutton-icon-only {
  padding: 0;
  overflow: hidden;

  .inside__icon-start,
  .inside__icon-center,
  .inside__icon-end {
    width: 18px;
    height: 18px;
    margin-right: 0;
    margin-left: 0;
    font-size: 18px;
  }
}

.wbutton-icon-only.wbutton-small {
  width: 32px !important;
  min-width: 32px !important;
}

.wbutton-icon-only.wbutton-large {
  width: 40px !important;
  min-width: 40px !important;
}

.wbutton-icon {
  width: 40px !important;
  min-width: 40px !important;
  padding: 0;
  overflow: hidden;
  .inside__icon-start,
  .inside__icon-end {
    margin: 0;
  }
  border-color: v-bind('tokens.color.action.utility.border') !important;
  color: v-bind('tokens.color.action.utility.text') !important;
  background-color: v-bind('tokens.color.action.utility.surface') !important;

  &:hover {
    background-color: v-bind('tokens.color.action.utility.hover') !important;
    border-color: v-bind('tokens.color.action.utility.border') !important;
    color: v-bind('tokens.color.action.utility.text') !important;
  }

  &:focus {
    background-color: v-bind('tokens.color.action.utility.active') !important;
    color: v-bind('tokens.color.action.utility.text') !important;
    border-color: v-bind('tokens.color.action.utility.border') !important;
  }

  &.wbutton-disabled {
    background: v-bind('tokens.color.action.utility.disabled.surface') !important;
    color: v-bind('tokens.color.action.utility.disabled.text') !important;
    border: none;
  }

  &.wbutton-inside-component {
    width: 22px !important;
    min-width: 22px !important;
  }
}

.wbutton-icon-secondary {
  &:hover {
    background-color: v-bind('tokens.color.background.surface.default') !important;
    border-color: v-bind('tokens.color.action.primary.active') !important;
    color: v-bind('tokens.color.action.primary.active') !important;
  }
}
</style>
